import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Tag,
  Typography,
  theme,
  Drawer,
  Button,
  ConfigProvider,
  theme as antTheme,
  Alert,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  BookOutlined,
  TeamOutlined,
  BarChartOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  CloseOutlined,
  ReadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/stores/authStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ROLE_STYLES } from '@/constants';
import NotificationBell from './NotificationBell';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const SIDEBAR_BG = '#1e293b';
const SIDEBAR_BORDER = 'rgba(255,255,255,0.08)';

const MENU_ITEMS = [
  { key: '/teacher', icon: <HomeOutlined />, label: 'Dashboard' },
  { key: '/teacher/decks', icon: <BookOutlined />, label: 'Decks' },
  { key: '/teacher/students', icon: <TeamOutlined />, label: 'Students' },
  { key: '/teacher/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
  { key: '/teacher/library', icon: <ReadOutlined />, label: 'Library' },
  { key: '/teacher/help-requests', icon: <QuestionCircleOutlined />, label: 'Help Requests' },
  { key: '/teacher/profile', icon: <UserOutlined />, label: 'Profile' },
];

function resolveSelectedKey(pathname: string): string {
  if (pathname === '/teacher' || pathname === '/teacher/') return '/teacher';
  const match = MENU_ITEMS.find(
    (item) => item.key !== '/teacher' && pathname.startsWith(item.key),
  );
  return match ? match.key : '/teacher';
}

export default function TeacherLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const { token: themeToken } = theme.useToken();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const queryClient = useQueryClient();

  const selectedKey = resolveSelectedKey(location.pathname);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
    if (isMobile) setSidebarOpen(false);
  };

  const handleLogout = () => {
    clearAuth();
    queryClient.clear();
    navigate('/login');
  };

  const avatarDropdownItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/teacher/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const roleStyle = ROLE_STYLES.TEACHER;

  const SidebarContent = () => (
    <>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 20px',
          gap: 10,
          borderBottom: `1px solid ${SIDEBAR_BORDER}`,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          M
        </div>
        <Text
          strong
          style={{
            color: '#fff',
            fontSize: 18,
            letterSpacing: '-0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          CardWise
        </Text>
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        items={MENU_ITEMS}
        onClick={handleMenuClick}
        style={{ marginTop: 8, borderRight: 0, background: 'transparent' }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Desktop Sidebar ── */}
      {!isMobile && (
        <ConfigProvider
          theme={{
            algorithm: antTheme.darkAlgorithm,
            token: { colorPrimary: '#6366f1', colorBgBase: SIDEBAR_BG },
            components: { Layout: { siderBg: SIDEBAR_BG } },
          }}
        >
          <Sider
            width={240}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            theme="dark"
            style={{
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 10,
              background: SIDEBAR_BG,
            }}
          >
            {collapsed ? (
              <>
                <div
                  style={{
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: `1px solid ${SIDEBAR_BORDER}`,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    M
                  </div>
                </div>
                <Menu
                  theme="dark"
                  mode="inline"
                  selectedKeys={[selectedKey]}
                  items={MENU_ITEMS}
                  onClick={handleMenuClick}
                  style={{ marginTop: 8, borderRight: 0, background: 'transparent' }}
                />
              </>
            ) : (
              <SidebarContent />
            )}
          </Sider>
        </ConfigProvider>
      )}

      {/* ── Mobile Sidebar Drawer ── */}
      {isMobile && (
        <ConfigProvider
          theme={{
            algorithm: antTheme.darkAlgorithm,
            token: { colorPrimary: '#6366f1', colorBgBase: SIDEBAR_BG },
            components: { Layout: { siderBg: SIDEBAR_BG } },
          }}
        >
          <Drawer
            title={null}
            placement="left"
            onClose={() => setSidebarOpen(false)}
            open={sidebarOpen}
            closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
            styles={{ body: { padding: 0, background: SIDEBAR_BG }, header: { background: SIDEBAR_BG, borderBottom: `1px solid ${SIDEBAR_BORDER}` } }}
            width={240}
          >
            <SidebarContent />
          </Drawer>
        </ConfigProvider>
      )}

      {/* ── Main area ── */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 240,
          transition: 'margin-left 0.2s',
          minHeight: '100vh',
        }}
      >
        <Header
          style={{
            background: themeToken.colorBgContainer,
            padding: isMobile ? '0 12px' : '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${themeToken.colorBorder}`,
            position: 'sticky',
            top: 0,
            zIndex: 9,
            height: isMobile ? 48 : 64,
            gap: 12,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setSidebarOpen(true)}
              style={{
                minWidth: 44,
                minHeight: 44,
                width: 44,
                height: 44,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                color: themeToken.colorTextSecondary,
              }}
            />
          ) : (
            <div
              style={{ cursor: 'pointer', fontSize: 18, color: themeToken.colorTextSecondary }}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flex: 1, justifyContent: 'flex-end' }}>
            {!isMobile && (
              <Tag
                style={{
                  color: roleStyle.color,
                  background: roleStyle.bg,
                  border: 'none',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Teacher
              </Tag>
            )}
            {!isMobile && (
              <Text style={{ fontWeight: 500, color: themeToken.colorTextBase }}>
                {user?.name ?? 'Teacher'}
              </Text>
            )}
            <NotificationBell size={isMobile ? 32 : 36} />
            <Dropdown menu={{ items: avatarDropdownItems }} trigger={['click']} placement="bottomRight">
              <Avatar
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
                size={isMobile ? 32 : 36}
              >
                {user?.name?.charAt(0)?.toUpperCase() ?? 'T'}
              </Avatar>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            padding: isMobile ? 12 : 24,
            flex: '1 1 auto',
          }}
        >
          {user?.teacherStatus && user.teacherStatus !== 'ACTIVE' && (
            <Alert
              type={user.teacherStatus === 'REJECTED' ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                user.teacherStatus === 'REJECTED'
                  ? 'Your teacher application has been rejected. Please contact support.'
                  : user.teacherStatus === 'SUSPENDED'
                  ? 'Your account has been suspended. Please contact support.'
                  : 'Your account is pending admin approval. You can prepare content now, but publishing will be enabled once approved.'
              }
            />
          )}
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
